import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { AlertCircle } from 'lucide-react'
import { useDashboardConfig, useDashboardEstadisticas, useDashboardFiltros, useDashboardTablaCompleta } from '../hooks/useDashboard'
import { DashboardHeader } from '../components/DashboardHeader'
import { dashboardApi } from '../api/dashboard.api'
import { DashboardStats } from '../components/DashboardStats'
import { DashboardCharts } from '../components/DashboardCharts'
import { DashboardFilters, type DashboardFilterValues } from '../components/DashboardFilters'
import { TablaFuente } from '../components/TablaFuente'
import { UsuarioDetalle } from '../components/UsuarioDetalle'
import { ComprobanteDetalle } from '../components/ComprobanteDetalle'
import { format } from 'date-fns'
import type { DashboardGraficos, DashboardTablaDatos } from '../types'

/** Parsea fecha dd/mm/yyyy a yyyy-mm-dd */
function parseFechaRegistro(fecha: string): string {
  const match = fecha.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (match) return `${match[3]}-${match[2]}-${match[1]}`
  return fecha.slice(0, 10)
}

/** Aplica filtros estándar (farmacia, provincia, promotor, fecha) a un array de registros */
function aplicarFiltrosEstandar(
  registros: Record<string, unknown>[],
  filtros?: Record<string, string>
): Record<string, unknown>[] {
  if (!filtros) return registros
  let result = registros
  if (filtros.farmacia) {
    const vals = filtros.farmacia.split(',').map((f) => f.trim())
    result = result.filter((r) => vals.includes(String(r['farmacia'] || '').trim()))
  }
  if (filtros.provincia) {
    const vals = filtros.provincia.split(',').map((p) => p.trim())
    result = result.filter((r) => vals.includes(String(r['provincia'] || '').trim()))
  }
  if (filtros.promotor) {
    const vals = filtros.promotor.split(',').map((p) => p.trim())
    result = result.filter((r) => vals.includes(String(r['promotor'] || '').trim()))
  }
  if (filtros.nombre_farmacia) {
    const vals = filtros.nombre_farmacia.split(',').map((f) => f.trim())
    result = result.filter((r) => vals.includes(String(r['nombre_farmacia'] || '').trim()))
  }
  if (filtros.direccion) {
    const vals = filtros.direccion.split(',').map((d) => d.trim())
    result = result.filter((r) => vals.includes(String(r['direccion'] || '').trim()))
  }
  if (filtros.fecha_desde || filtros.fecha_hasta) {
    result = result.filter((r) => {
      const fechaStr = parseFechaRegistro(String(r['fecha'] || ''))
      if (filtros.fecha_desde && fechaStr < filtros.fecha_desde) return false
      if (filtros.fecha_hasta && fechaStr > filtros.fecha_hasta) return false
      return true
    })
  }
  return result
}

/** Verifica si un medicamento está oculto (coincidencia parcial case-insensitive) */
function esMedicamentoOculto(nombre: string, ocultos: string[]): boolean {
  if (!ocultos.length) return false
  const lower = nombre.toLowerCase()
  return ocultos.some((o) => lower.includes(o.toLowerCase()))
}

/** Construye función normalizadora de medicamentos a partir de config. Si no hay config, retorna identity. */
function buildNormalizarMedicamento(
  config?: { nombre: string; patron: string }[]
): (raw: string) => string {
  if (!config || config.length === 0) return (raw) => raw
  const pares: [string, RegExp][] = config.map((c) => [c.nombre, new RegExp(c.patron, 'i')])
  return (raw: string) => {
    for (const [nombre, regex] of pares) {
      if (regex.test(raw)) return nombre
    }
    return raw
  }
}

/** Calcula gráficas client-side desde los registros completos de allData */
function computarGraficosClientSide(
  allData: DashboardTablaDatos,
  tituloTab: string,
  filtros?: Record<string, string>,
  normalizarMedicamento: (raw: string) => string = (raw) => raw,
  rolTab?: string,
  mostrarPuntosParticipantes?: boolean,
  medicamentosOcultos?: string[],
): DashboardGraficos {
  const tLower = tituloTab.toLowerCase()
  const esPuntajes = tLower.includes('puntaje') || tLower.includes('punto')
  const esVentas = tLower.includes('venta')
  const esPrincipal = rolTab === 'principal'

  let registros = aplicarFiltrosEstandar(allData.results as Record<string, unknown>[], filtros)

  // Filtrar medicamentos ocultos en tabs de ventas
  if (esVentas && medicamentosOcultos && medicamentosOcultos.length > 0) {
    registros = registros.filter((reg) => {
      const med = normalizarMedicamento(String(reg['medicamento'] || '').trim())
      return !esMedicamentoOculto(med, medicamentosOcultos)
    })
  }

  // por_fecha: agrupar por fecha
  const fechaMap = new Map<string, number>()
  for (const reg of registros) {
    const fecha = parseFechaRegistro(String(reg['fecha'] || ''))
    if (!fecha || fecha.length < 10) continue
    const valor = esPuntajes ? (Number(reg['puntos']) || 0) : esVentas ? (Number(reg['cantidad']) || 0) : 1
    fechaMap.set(fecha, (fechaMap.get(fecha) || 0) + valor)
  }
  const porFecha = Array.from(fechaMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fecha, total]) => ({ fecha, total }))

  // Barras por campo
  function agruparPorCampo(campo: string): { nombre: string; total: number }[] {
    const counts = new Map<string, number>()
    for (const reg of registros) {
      let val = String(reg[campo] || '').trim()
      if (!val) continue
      if (campo === 'medicamento') val = normalizarMedicamento(val)
      const valor = esPuntajes ? (Number(reg['puntos']) || 0) : esVentas ? (Number(reg['cantidad']) || 0) : 1
      counts.set(val, (counts.get(val) || 0) + valor)
    }
    return Array.from(counts.entries())
      .sort(([, a], [, b]) => b - a)
      .map(([nombre, total]) => ({ nombre, total }))
  }

  const graficas: DashboardGraficos['graficas'] = []

  // Para tab principal (Participantes): gráfica especial de puntos por participante (solo si configurado)
  let participantesPuntos: { nombre: string; total: number }[] | undefined
  if (esPrincipal && mostrarPuntosParticipantes) {
    participantesPuntos = registros
      .map((reg) => ({
        nombre: String(reg['nombre completo'] || '').trim(),
        total: Number(reg['total']?.toString().replace(/[^\d.]/g, '') || 0),
      }))
      .filter((p) => p.nombre)
      .sort((a, b) => b.total - a.total)
  }

  if (esVentas) {
    graficas.push({ titulo: 'Medicamentos', datos: agruparPorCampo('medicamento') })
    graficas.push({ titulo: 'Promotores', datos: agruparPorCampo('promotor') })
  } else if (esPuntajes) {
    graficas.push({ titulo: 'Promotores', datos: agruparPorCampo('promotor') })
  } else {
    graficas.push({ titulo: 'Promotores', datos: agruparPorCampo('promotor') })
  }

  graficas.push({ titulo: 'Provincias', datos: agruparPorCampo('provincia') })
  graficas.push({ titulo: 'Farmacias', datos: agruparPorCampo('farmacia') })
  if (!esVentas && !esPuntajes) {
    const datosFarmacia = agruparPorCampo('nombre_farmacia')
    if (datosFarmacia.length > 0) graficas.push({ titulo: 'Nombre Farmacia', datos: datosFarmacia })
    const datosDireccion = agruparPorCampo('direccion')
    if (datosDireccion.length > 0) graficas.push({ titulo: 'Dirección', datos: datosDireccion })
  }

  return { por_fecha: porFecha, graficas, participantes_puntos: participantesPuntos }
}

function DashboardSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-md" />
        <div>
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-48 mt-1" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-28 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-10 w-96" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  )
}

function DashboardError({ error }: { error: Error }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
      <h2 className="text-xl font-semibold mb-2">Error al cargar el dashboard</h2>
      <p className="text-muted-foreground max-w-md">
        {error.message || 'No se pudo cargar la configuracion del dashboard. Verifique que el grupo existe y tiene permisos.'}
      </p>
    </div>
  )
}

/** Precarga datos completos de todas las fuentes en paralelo (máx 5) */
function usePrecargaTodasLasFuentes(grupoId: string, fuentes: { config_id: number }[]) {
  // Llamar hooks para cada fuente posible (máximo 5 fuentes)
  const q0 = useDashboardTablaCompleta(grupoId, fuentes[0]?.config_id ?? 0)
  const q1 = useDashboardTablaCompleta(grupoId, fuentes[1]?.config_id ?? 0)
  const q2 = useDashboardTablaCompleta(grupoId, fuentes[2]?.config_id ?? 0)
  const q3 = useDashboardTablaCompleta(grupoId, fuentes[3]?.config_id ?? 0)
  const q4 = useDashboardTablaCompleta(grupoId, fuentes[4]?.config_id ?? 0)

  const queries = [q0, q1, q2, q3, q4]
  const map = new Map<number, { data: typeof q0.data; isLoading: boolean }>()
  fuentes.forEach((f, i) => {
    if (i < 5) {
      map.set(f.config_id, { data: queries[i].data, isLoading: queries[i].isLoading })
    }
  })
  return map
}

export function DashboardPersonalizadoPage() {
  const { grupoId } = useParams<{ grupoId: string }>()
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedComprobanteId, setSelectedComprobanteId] = useState<number | null>(null)
  const [selectedMedicamento, setSelectedMedicamento] = useState<string | undefined>(undefined)
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [activeTab, setActiveTab] = useState<string>('')
  const [filters, setFilters] = useState<DashboardFilterValues>({})
  const [isExporting, setIsExporting] = useState(false)

  const {
    data: dashboardConfig,
    isLoading: configLoading,
    error: configError,
  } = useDashboardConfig(grupoId || '')

  // Convert filter values to API params (before hooks that depend on it)
  const filterParams = useMemo(() => {
    const params: Record<string, string> = {}
    if (filters.dateRange?.from) {
      params.fecha_desde = format(filters.dateRange.from, 'yyyy-MM-dd')
    }
    if (filters.dateRange?.to) {
      params.fecha_hasta = format(filters.dateRange.to, 'yyyy-MM-dd')
    }
    if (filters.farmacias && filters.farmacias.length > 0) {
      params.farmacia = filters.farmacias.join(',')
    }
    if (filters.provincias && filters.provincias.length > 0) {
      params.provincia = filters.provincias.join(',')
    }
    if (filters.promotores && filters.promotores.length > 0) {
      params.promotor = filters.promotores.join(',')
    }
    if (filters.nombre_farmacias && filters.nombre_farmacias.length > 0) {
      params.nombre_farmacia = filters.nombre_farmacias.join(',')
    }
    if (filters.direcciones && filters.direcciones.length > 0) {
      params.direccion = filters.direcciones.join(',')
    }
    return Object.keys(params).length > 0 ? params : undefined
  }, [filters])

  // Filtro registro_tonal se aplica solo client-side
  const filtroRegistroTonal = filters.registroTonal

  const {
    data: estadisticas,
    isLoading: statsLoading,
    refetch: refetchStats,
    isFetching: isRefreshing,
    dataUpdatedAt,
  } = useDashboardEstadisticas(grupoId || '', filterParams)

  const activeConfigIdNum = activeTab ? Number(activeTab) : undefined
  const {
    data: filtrosOpciones,
    isLoading: filtrosLoading,
  } = useDashboardFiltros(grupoId || '', activeConfigIdNum)

  // Precarga de datos completos de todas las tabs en paralelo
  const precargaMap = usePrecargaTodasLasFuentes(grupoId || '', dashboardConfig?.fuentes ?? [])

  // Identificar fuente principal una vez
  const fuentePrincipal = useMemo(() =>
    dashboardConfig?.fuentes.find((f) => f.tabla.toLowerCase().includes('registros'))
      ?? dashboardConfig?.fuentes[0]
  , [dashboardConfig])

  // Set de nombres que pasan el filtro registroTonal (para cruzar con tabs secundarias)
  const nombresRegistroTonal = useMemo(() => {
    if (!filtroRegistroTonal || !fuentePrincipal) return null
    const precarga = precargaMap.get(fuentePrincipal.config_id)
    if (!precarga?.data?.results) return null
    const rows = precarga.data.results as Record<string, unknown>[]
    const filtrados = rows.filter((r) => {
      const esValidado = String(r['estado'] || '').toLowerCase() === 'validado'
      return filtroRegistroTonal === 'validado' ? esValidado : !esValidado
    })
    return new Set(filtrados.map((r) => String(r['nombre completo'] || '').toLowerCase().trim()))
  }, [filtroRegistroTonal, fuentePrincipal, precargaMap])

  // Pre-filtrar allData por registroTonal para cada tab
  const getFilteredAllData = useCallback((configId: number): DashboardTablaDatos | undefined => {
    const precarga = precargaMap.get(configId)
    if (!precarga?.data) return undefined
    if (!nombresRegistroTonal) return precarga.data // Sin filtro tonal, datos originales

    const rows = precarga.data.results as Record<string, unknown>[]
    let filtrados: Record<string, unknown>[]

    if (fuentePrincipal && configId === fuentePrincipal.config_id) {
      // Tabla principal: filtrar directamente por columna estado
      filtrados = rows.filter((r) => {
        const esValidado = String(r['estado'] || '').toLowerCase() === 'validado'
        return filtroRegistroTonal === 'validado' ? esValidado : !esValidado
      })
    } else {
      // Tablas secundarias: filtrar por nombre completo cruzado
      filtrados = rows.filter((r) =>
        nombresRegistroTonal.has(String(r['nombre completo'] || '').toLowerCase().trim())
      )
    }

    return { ...precarga.data, results: filtrados, count: filtrados.length }
  }, [precargaMap, nombresRegistroTonal, fuentePrincipal, filtroRegistroTonal])

  // Gráficas calculadas client-side desde allData pre-filtrada de la tab activa
  const activeConfigId = activeTab ? Number(activeTab) : 0
  const activeFuente = dashboardConfig?.fuentes.find((f) => f.config_id === activeConfigId)
  const activeFilteredData = useMemo(() => getFilteredAllData(activeConfigId), [getFilteredAllData, activeConfigId])

  const normalizarMedicamento = useMemo(
    () => buildNormalizarMedicamento(dashboardConfig?.medicamentos_canonicos),
    [dashboardConfig?.medicamentos_canonicos]
  )

  const graficos = useMemo<DashboardGraficos | undefined>(() => {
    if (!activeFilteredData || !activeFuente) return undefined
    return computarGraficosClientSide(activeFilteredData, activeFuente.titulo, filterParams, normalizarMedicamento, activeFuente.rol, dashboardConfig?.mostrar_puntos_participantes, dashboardConfig?.medicamentos_ocultos)
  }, [activeFilteredData, activeFuente, filterParams, normalizarMedicamento, dashboardConfig?.mostrar_puntos_participantes, dashboardConfig?.medicamentos_ocultos])

  const graficosLoading = !!activeConfigId && (precargaMap.get(activeConfigId)?.isLoading ?? true)

  // Recalcular métricas client-side desde precarga (canónicos y puntos de canjes)
  const metricasPrecarga = useMemo(() => {
    if (!estadisticas) return { tiposMedicamentosCanonicos: null, puntosCanjes: null }

    // Tipos de medicamentos con canónicos
    let tiposMedicamentosCanonicos: number | null = null
    const canonicos = dashboardConfig?.medicamentos_canonicos
    if (canonicos && canonicos.length > 0) {
      const fuenteVentas = estadisticas.fuentes.find((f) => f.tabla.toLowerCase().includes('ventas'))
      if (fuenteVentas) {
        const precarga = precargaMap.get(fuenteVentas.config_id)
        if (precarga?.data?.results) {
          const rows = aplicarFiltrosEstandar(precarga.data.results as Record<string, unknown>[], filterParams)
          const normFn = buildNormalizarMedicamento(canonicos)
          const ocultos = dashboardConfig?.medicamentos_ocultos || []
          const nombresNormalizados = new Set(
            rows.map((r) => normFn(String(r['medicamento'] || '').trim())).filter((n) => n && !esMedicamentoOculto(n, ocultos))
          )
          tiposMedicamentosCanonicos = nombresNormalizados.size
        }
      }
    }

    // Suma de puntos de canjes
    let puntosCanjes: number | null = null
    const fuenteCanjes = estadisticas.fuentes.find((f) => f.tabla.toLowerCase().includes('canjes'))
    if (fuenteCanjes) {
      const precarga = precargaMap.get(fuenteCanjes.config_id)
      if (precarga?.data?.results) {
        const rows = aplicarFiltrosEstandar(precarga.data.results as Record<string, unknown>[], filterParams)
        puntosCanjes = rows.reduce((sum, r) => {
          const val = Number(r['puntos']) || Number(r['puntos_canjeados']) || 0
          return sum + val
        }, 0)
      }
    }

    return { tiposMedicamentosCanonicos, puntosCanjes }
  }, [dashboardConfig?.medicamentos_canonicos, estadisticas, precargaMap, filterParams])

  // Recalcular stats client-side cuando hay filtro registroTonal (afecta TODAS las cards)
  const estadisticasAjustadas = useMemo(() => {
    const { tiposMedicamentosCanonicos, puntosCanjes } = metricasPrecarga

    if (!estadisticas || !nombresRegistroTonal || !fuentePrincipal) {
      // Sin filtro registroTonal, solo ajustar métricas de precarga
      if (estadisticas && (tiposMedicamentosCanonicos != null || puntosCanjes != null)) {
        return {
          ...estadisticas,
          ...(tiposMedicamentosCanonicos != null && { tipos_medicamentos: tiposMedicamentosCanonicos }),
          ...(puntosCanjes != null && { puntos_canjes: puntosCanjes }),
        }
      }
      return estadisticas
    }

    let puntosActividad: number | null = null
    let puntosVentas: number | null = null
    let tiposMedicamentos: number | null = null
    let totalCantidadVentas: number | null = null
    let puntosCanjesAjustados: number | null = null

    const nuevasFuentes = estadisticas.fuentes.map((f) => {
      const filtered = getFilteredAllData(f.config_id)
      if (!filtered) return f

      // Aplicar también los filtros estándar (farmacia, provincia, etc.)
      const rows = aplicarFiltrosEstandar(
        filtered.results as Record<string, unknown>[],
        filterParams
      )
      const tLower = f.tabla.toLowerCase()
      if (tLower.includes('puntajes')) {
        puntosActividad = rows.reduce((sum, r) => sum + (Number(r['puntos']) || 0), 0)
      } else if (tLower.includes('ventas')) {
        const normFn = buildNormalizarMedicamento(dashboardConfig?.medicamentos_canonicos)
        const ocultos = dashboardConfig?.medicamentos_ocultos || []
        const rowsFiltradas = ocultos.length > 0
          ? rows.filter((r) => !esMedicamentoOculto(normFn(String(r['medicamento'] || '').trim()), ocultos))
          : rows
        puntosVentas = rowsFiltradas.reduce((sum, r) => sum + (Number(r['puntos']) || 0), 0)
        const nombresNormalizados = new Set(
          rowsFiltradas.map((r) => normFn(String(r['medicamento'] || '').trim())).filter(Boolean)
        )
        tiposMedicamentos = nombresNormalizados.size
        totalCantidadVentas = rowsFiltradas.reduce((sum, r) => sum + (Number(r['cantidad']) || 0), 0)
      } else if (tLower.includes('canjes')) {
        puntosCanjesAjustados = rows.reduce((sum, r) => sum + (Number(r['puntos']) || Number(r['puntos_canjeados']) || 0), 0)
      }
      return { ...f, total: rows.length }
    })

    return {
      ...estadisticas,
      fuentes: nuevasFuentes,
      puntos_actividad: puntosActividad ?? estadisticas.puntos_actividad,
      puntos_ventas: puntosVentas ?? estadisticas.puntos_ventas,
      tipos_medicamentos: tiposMedicamentos ?? tiposMedicamentosCanonicos ?? estadisticas.tipos_medicamentos,
      total_cantidad_ventas: totalCantidadVentas ?? estadisticas.total_cantidad_ventas,
      puntos_canjes: puntosCanjesAjustados ?? puntosCanjes ?? undefined,
    }
  }, [estadisticas, nombresRegistroTonal, fuentePrincipal, getFilteredAllData, filterParams, metricasPrecarga])

  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt))
    }
  }, [dataUpdatedAt])

  useEffect(() => {
    if (dashboardConfig?.fuentes[0] && !activeTab) {
      setActiveTab(String(dashboardConfig.fuentes[0].config_id))
    }
  }, [dashboardConfig, activeTab])

  const handleExportarCompleto = useCallback(async () => {
    if (!grupoId || isExporting) return
    setIsExporting(true)
    try {
      await dashboardApi.exportar(grupoId)
    } catch (error) {
      console.error('Error al exportar:', error)
    } finally {
      setIsExporting(false)
    }
  }, [grupoId, isExporting])

  const handleExportarFiltrado = useCallback(async () => {
    if (!grupoId || isExporting) return
    setIsExporting(true)
    try {
      await dashboardApi.exportar(grupoId, filterParams)
    } catch (error) {
      console.error('Error al exportar:', error)
    } finally {
      setIsExporting(false)
    }
  }, [grupoId, filterParams, isExporting])

  // Conteos para el popover de exportar (solo fuente principal = registros)
  const totalRegistros = fuentePrincipal
    ? precargaMap.get(fuentePrincipal.config_id)?.data?.count
    : undefined
  const fuentePrincipalAjustada = estadisticasAjustadas?.fuentes.find((f) => f.tabla.toLowerCase().includes('registros'))
  const totalFiltrados = fuentePrincipalAjustada?.total
  const hayFiltrosActivos = !!(filterParams || filtroRegistroTonal)

  if (configLoading) return <DashboardSkeleton />
  if (configError) return <DashboardError error={configError as Error} />
  if (!dashboardConfig) return null

  const handleRefresh = () => {
    refetchStats()
  }

  const handleUsuarioClick = (userId: number) => {
    setSelectedUserId(userId)
  }

  const handleComprobanteClick = (comprobanteId: number, medicamento?: string) => {
    setSelectedComprobanteId(comprobanteId)
    setSelectedMedicamento(medicamento)
  }

  return (
    <div className="space-y-3">
      <DashboardHeader
        config={dashboardConfig}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastUpdated={lastUpdated}
        onExportarCompleto={handleExportarCompleto}
        onExportarFiltrado={handleExportarFiltrado}
        isExporting={isExporting}
        totalRegistros={totalRegistros}
        totalFiltrados={totalFiltrados}
        hayFiltrosActivos={hayFiltrosActivos}
      />

      <DashboardStats
        estadisticas={estadisticasAjustadas}
        isLoading={statsLoading}
        colorPrimario={dashboardConfig.color_primario}
        tabs={dashboardConfig.tabs}
        statsConfig={dashboardConfig.stats_config}
        formatoMoneda={dashboardConfig.formato_moneda}
        valorPuntoCanje={dashboardConfig.valor_punto_canje}
      />

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setFilters({}) }}>
        <TabsList className="w-full">
          {dashboardConfig.fuentes.map((fuente) => (
            <TabsTrigger
              key={fuente.config_id}
              value={String(fuente.config_id)}
              className="flex-1 transition-all duration-200 data-[state=active]:text-white data-[state=active]:shadow"
              style={activeTab === String(fuente.config_id)
                ? { backgroundColor: dashboardConfig.color_primario }
                : undefined}
            >
              {fuente.titulo}
            </TabsTrigger>
          ))}
        </TabsList>

        <DashboardFilters
          filters={filters}
          onFiltersChange={setFilters}
          opciones={filtrosOpciones}
          isLoading={filtrosLoading}
          showEstado={activeFuente?.tabla?.toLowerCase().includes('registros') ?? false}
        />

        <DashboardCharts
          data={graficos}
          isLoading={graficosLoading}
          activeConfigId={activeTab}
          tooltipLabel={
            (() => {
              const fuente = dashboardConfig.fuentes.find((f) => String(f.config_id) === activeTab)
              const t = (fuente?.titulo || '').toLowerCase()
              if (t.includes('puntaje') || t.includes('punto')) return 'puntos'
              if (t.includes('venta')) return 'ventas'
              return 'registros'
            })()
          }
          isCurrency={
            dashboardConfig.formato_moneda
              ? (() => {
                  const fuente = dashboardConfig.fuentes.find((f) => String(f.config_id) === activeTab)
                  const t = (fuente?.titulo || '').toLowerCase()
                  return t.includes('venta')
                })()
              : false
          }
        />

        {dashboardConfig.fuentes.map((fuente) => {
          const precarga = precargaMap.get(fuente.config_id)
          const esVentas = fuente.titulo.toLowerCase().includes('venta')
          let tabAllData = getFilteredAllData(fuente.config_id)
          // Normalizar y filtrar medicamentos ocultos en la tabla de ventas
          if (esVentas && tabAllData) {
            tabAllData = {
              ...tabAllData,
              results: tabAllData.results
                .map((r) => ({
                  ...r,
                  medicamento: r['medicamento'] ? normalizarMedicamento(String(r['medicamento'])) : r['medicamento'],
                }))
                .filter((r) => !esMedicamentoOculto(String(r['medicamento'] || ''), dashboardConfig.medicamentos_ocultos || [])),
            }
          }
          return (
            <TabsContent key={fuente.config_id} value={String(fuente.config_id)} className="mt-4">
              <TablaFuente
                grupoId={grupoId || ''}
                configId={fuente.config_id}
                titulo={fuente.titulo}
                camposMostrar={fuente.campos_mostrar}
                onUsuarioClick={handleUsuarioClick}
                onComprobanteClick={handleComprobanteClick}
                isCurrency={!!dashboardConfig.formato_moneda && esVentas}
                filtros={filterParams}
                allData={tabAllData}
                allDataLoading={precarga?.isLoading}
                valorPuntoCanje={dashboardConfig.valor_punto_canje}
              />
            </TabsContent>
          )
        })}
      </Tabs>

      {/* Sheet de detalle de usuario */}
      {selectedUserId && (
        <UsuarioDetalle
          grupoId={grupoId || ''}
          userId={selectedUserId}
          open={!!selectedUserId}
          onOpenChange={(open) => {
            if (!open) setSelectedUserId(null)
          }}
        />
      )}

      {/* Sheet de detalle de comprobante/factura */}
      {selectedComprobanteId && (
        <ComprobanteDetalle
          grupoId={grupoId || ''}
          comprobanteId={selectedComprobanteId}
          open={!!selectedComprobanteId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedComprobanteId(null)
              setSelectedMedicamento(undefined)
            }
          }}
          medicamentoResaltar={selectedMedicamento}
        />
      )}
    </div>
  )
}
